import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import { Box, Button, Card, CardActions, CardContent, Grid, TextField, Typography } from '@mui/material';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import {
  createBillingAccountNote,
  deleteBillingAccountNote,
  editBillingAccountNote,
} from '@sophocles/features/billing/api';
import { selectBillingAccount, selectBillingNotesForAccount } from '@sophocles/features/billing/slice';

function formatDate(date) {
  return date ? format(new Date(date), 'PPPpp') : '-';
}

function AccountBillingNotes({ accountId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  const account = useSelector(selectBillingAccount(accountId));
  const notes = useSelector(selectBillingNotesForAccount(accountId));

  const handleDeleteNote = id => {
    dispatch(deleteBillingAccountNote(id));
  };

  return (
    <div>
      <Grid container sx={{ mb: 3 }} justifyContent="space-between" alignItems="center">
        <Typography variant="h5" component="div">
          {t('billing.notes', 'Notes')}
        </Typography>
        <Button
          variant="contained"
          disabled={account == null || isDialogOpen}
          onClick={() => {
            setSelectedNote({ account: accountId, text: '' });
            setIsDialogOpen(true);
          }}
        >
          {t('billing.addNote', 'Add Note')}
        </Button>
      </Grid>
      {accountId === null ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            my: 2,
          }}
        >
          <NotesOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
          <Typography color="textSecondary">
            {t('billing.noAccountSelectedNotes', 'No account selected, unable to display notes.')}
          </Typography>
        </Box>
      ) : notes == null || notes.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            my: 2,
          }}
        >
          <NotesOutlinedIcon color="disabled" sx={{ fontSize: 40 }} />
          <Typography color="textSecondary">
            {t('billing.noNotesToDisplay', 'No notes to display for this account.')}
          </Typography>
        </Box>
      ) : (
        notes.map(note => (
          <Card key={note.id} sx={{ marginBottom: 2 }}>
            <CardContent>
              <Typography variant="body1" component="div">
                {t('billing.noteColon', 'Note:')} {note.text}
              </Typography>
              <Typography color="textSecondary">
                {t('billing.createdAtColon', 'Created At:')} {formatDate(note.created_at)}
              </Typography>
              <Typography color="textSecondary">
                {t('billing.updatedAtColon', 'Updated At:')} {formatDate(note.updated_at)}
              </Typography>
              <Typography color="textSecondary">
                {t('billing.userColon', 'User:')} {note.created_by.first_name} {note.created_by.last_name} (ID:
                {note.created_by.id})
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                size="small"
                disabled={isDialogOpen}
                onClick={() => {
                  setSelectedNote(note);
                  setIsDialogOpen(true);
                }}
              >
                {t('billing.edit', 'Edit')}
              </Button>
              <Button size="small" color="error" disabled={isDialogOpen} onClick={() => handleDeleteNote(note.id)}>
                {t('billing.delete', 'Delete')}
              </Button>
            </CardActions>
          </Card>
        ))
      )}
      <ResponsiveDialog
        title={
          selectedNote?.id == null
            ? t('billing.newBillingAccountNote', 'New Billing Account Note')
            : t('billing.editBillingAccountNote', 'Edit Billing Account Note')
        }
        open={isDialogOpen}
        onSubmit={() => {
          const isCreating = selectedNote?.id == null;
          if (isCreating) {
            dispatch(createBillingAccountNote(selectedNote));
          } else {
            dispatch(editBillingAccountNote(selectedNote));
          }
          setIsDialogOpen(false);
        }}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedNote(null);
        }}
      >
        <TextField
          multiline
          label={t('billing.note', 'Note')}
          rows={4}
          variant="outlined"
          fullWidth
          value={selectedNote?.text}
          onChange={e => {
            setSelectedNote({ ...selectedNote, text: e.target.value });
          }}
        />
      </ResponsiveDialog>
    </div>
  );
}

export default AccountBillingNotes;
